import React, { createContext, useContext, useMemo } from 'react';
import useLocale from '@/hooks/useLocale';
import type {
  BannerProps,
  BannerType,
  TitleProps,
  ToggleRowProps,
  MetricsProps,
  TeamAlertProps,
  SummaryProps,
  SummaryContextValue,
} from './Summary.types';
import * as Styles from './Summary.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import { format } from 'date-fns';
import bannerCriticalPath from '../../media/banner-critical.svg';
import bannerSuccessPath from '../../media/banner-success.svg';
import bannerWarningPath from '../../media/banner-warning.svg';
import bannerGradientPath from '../../media/banner-gradient.svg';
import type { LocaleWithGatorProtectAAClient } from '../../gatorProtect.types';

import {
  AlertV2,
  IconWarning,
  Skeleton,
  Toggle,
  Tooltip,
} from 'gatorcomponents';

type BannerConfigEntry = {
  icon: string;
  bannerType: string;
  alt: string;
  testId: string;
};

const SummaryContext = createContext<SummaryContextValue>({
  content: {} as SummaryContextValue['content'],
  loading: false,
});
const useSummaryContext = () => useContext(SummaryContext);

const BANNER_CONFIG: Record<BannerType, BannerConfigEntry> = {
  trialActive: {
    icon: bannerSuccessPath,
    bannerType: 'success',
    alt: 'Trial Active',
    testId: 'trial-active-message',
  },
  cleaningInProgress: {
    icon: bannerWarningPath,
    bannerType: 'warning',
    alt: 'Cleaning in Progress',
    testId: 'cleaning-in-progress-message',
  },
  limitedProtection: {
    icon: bannerWarningPath,
    bannerType: 'warning',
    alt: 'Limited Protection',
    testId: 'limited-protection-message',
  },
  fullProtectionActive: {
    icon: bannerGradientPath,
    bannerType: 'success-gradient',
    alt: 'Full Protection Active',
    testId: 'full-protection-active-message',
  },
  expiredProtection: {
    icon: bannerCriticalPath,
    bannerType: 'critical',
    alt: 'Expired Protection',
    testId: 'expired-protection-message',
  },
  aaTrialActive: {
    icon: bannerSuccessPath,
    bannerType: 'success',
    alt: 'AA Trial Active',
    testId: 'aa-trial-active-message',
  },
};

const Banner = ({ type, trialDaysLeft, expireDate }: BannerProps) => {
  const { content } = useSummaryContext();
  const { gatorProtect } = useLocale() as LocaleWithGatorProtectAAClient;
  const config = BANNER_CONFIG[type];
  if (!config) return null;

  const aaClientBanner = gatorProtect?.protectionCard?.aaClient?.trialActive?.banner;
  const formattedExpireDate = expireDate ? format(new Date(expireDate), 'dd/MM/yyyy') : '';

  const contentMap: Record<BannerType, React.ReactNode> = {
    trialActive: content.alerts.trialActive(trialDaysLeft ?? 0),
    cleaningInProgress: content.alerts.cleaningInProgress,
    limitedProtection: content.alerts.limitedProtection,
    fullProtectionActive: content.alerts.fullProtectionActive,
    expiredProtection: content.alerts.expiredProtection,
    aaTrialActive: aaClientBanner ? (
      <>
        <strong>{aaClientBanner.boldPart}</strong>
        {` ${aaClientBanner.regularPart} `}
        <strong>{`${formattedExpireDate}!`}</strong>
      </>
    ) : null,
  };

  const isFullProtection = type === 'fullProtectionActive';

  return (
    <Styles.StatusBanner type={config.bannerType} data-testid={config.testId}>
      <Styles.StatusIcon src={config.icon} alt={config.alt} />
      {isFullProtection ? (
        <Styles.FullProtectionBannerContent>
          <Styles.FullProtectionBannerText>{contentMap[type]}</Styles.FullProtectionBannerText>
        </Styles.FullProtectionBannerContent>
      ) : (
        contentMap[type]
      )}
    </Styles.StatusBanner>
  );
};

const Title = ({ loading = false }: TitleProps) => {
  const { content } = useSummaryContext();

  if (!content.title) {
    return null;
  }

  if (loading) {
    return (
      <Styles.TitleSkeletonWrapper>
        <Skeleton height="100%" width="240px" />
      </Styles.TitleSkeletonWrapper>
    );
  }

  return <Styles.Title>{content.title}</Styles.Title>;
};

export const ToggleRow = ({
  enabled,
  onToggle,
  lastScan,
  loading,
}: ToggleRowProps) => {
  const { content } = useSummaryContext();

  if (loading) {
    return (
      <Skeleton width="440px" height="40px" testId="summary-toggle-row-skeleton" />
    );
  }

  if (enabled === undefined || typeof onToggle !== 'function' || !lastScan) return null;

  const lastScanData = () => lastScan && (
    <Styles.ToggleColumn>
      <Styles.Label>
        {content.automaticRemovalToggle.automaticRemovalLabel(enabled)}
      </Styles.Label>

      <Styles.LabelTime>
        {lastScan && content.automaticRemovalToggle.lastVerification({
          hour: lastScan ? format(new Date(lastScan), "HH'h'mm") : '--',
          day: lastScan ? format(new Date(lastScan), 'dd/MM/yyyy') : '--',
        })}
      </Styles.LabelTime>
    </Styles.ToggleColumn>
  );

  return (
    <Styles.TitleWithToggleRow>
      <Styles.AutomaticRemovalRow data-testid="AutomaticRemovalRow">
        <Tooltip
          content={content.automaticRemovalToggle.automaticRemovalTooltip}
          preferredPosition="topLeft"
        >
          <Styles.ToggleLabel onClick={onToggle}>
            <Toggle
              testId="automatic-cleaning-toggle"
              checked={enabled}
              controlledByFather
            />

            {lastScanData()}
          </Styles.ToggleLabel>
        </Tooltip>
      </Styles.AutomaticRemovalRow>
    </Styles.TitleWithToggleRow>
  );
};

const Metrics = ({
  threatsIdentified,
  threatsRemediated,
  onToggle,
  enabled,
  lastScan,
  hasToggle,
}: MetricsProps) => {
  const { content, loading } = useSummaryContext();

  const memoizedMetrics = useMemo(() => {
    const showMetrics = !!lastScan || loading;
    const showNotScannedMessage = !lastScan && !loading;
    const showLastScanData = !!lastScan && !loading && !hasToggle;

    return (
      <Styles.Content>
        {showMetrics && (
          <Styles.Row>
            <Styles.Column>
              <Styles.ColumnTextWrapper>
                <Styles.ColumnTitle>{content.threatsIdentified}</Styles.ColumnTitle>

                <Styles.ColumnValue data-testid="identified">
                  {loading ? <Skeleton height="40px" width="80px" /> : (threatsIdentified ?? 0)}
                </Styles.ColumnValue>
              </Styles.ColumnTextWrapper>
            </Styles.Column>

            <Styles.Column>
              <Styles.Divider />
            </Styles.Column>

            <Styles.Column>
              <Styles.ColumnTextWrapper>
                <Styles.ColumnTitle>
                  {content.threatsRemediated}

                  {!enabled && (
                    <Styles.ColumnIcon>
                      <IconWarning color="problemPure" />
                    </Styles.ColumnIcon>
                  )}
                </Styles.ColumnTitle>

                <Styles.ColumnValue
                  data-testid="remediated"
                  $grey={(threatsIdentified ?? 0) > (threatsRemediated ?? 0) || !enabled}
                  $green={(threatsRemediated ?? 0) > 0}
                >
                  {loading
                    ? <Skeleton height="40px" width="80px" />
                    : (threatsRemediated ?? 0)
                  }

                  <Styles.EnableYourProtection>
                    {content.enableYourProtection}
                  </Styles.EnableYourProtection>
                </Styles.ColumnValue>
              </Styles.ColumnTextWrapper>
            </Styles.Column>
          </Styles.Row>
        )}

        {showNotScannedMessage && (
          <Styles.WithoutLastScanRow>
            <Styles.LastScanTitle>{content.withoutLastScan.title}</Styles.LastScanTitle>
            <Styles.LastScanDescription>{content.withoutLastScan.description}</Styles.LastScanDescription>
          </Styles.WithoutLastScanRow>
        )}

        {showLastScanData && (
          <Styles.LastScanInfo>
            <Styles.LastScanData>
              {content.automaticRemovalToggle.lastVerification({
                hour: lastScan ? format(new Date(lastScan), "HH'h'mm") : '--',
                day: lastScan ? format(new Date(lastScan), 'dd/MM/yyyy') : '--',
              })}
            </Styles.LastScanData>
          </Styles.LastScanInfo>
        )}

        <ToggleRow
          enabled={enabled}
          onToggle={onToggle}
          lastScan={lastScan}
          loading={loading}
        />
      </Styles.Content>
    );
  }, [threatsIdentified, threatsRemediated, onToggle, enabled, lastScan, hasToggle, content, loading]);

  return memoizedMetrics;
};

const TeamAlert = ({ variant, threatsNumber, onOpenTicket }: TeamAlertProps) => {
  const { content } = useSummaryContext();

  if (variant === 'intervention') {
    return (
      <AlertV2
        title={content.alerts.teamIntervention.title(threatsNumber ?? 0)}
        description={content.alerts.teamIntervention.description}
        primaryButtonLabel={content.alerts.teamIntervention.buttonLabel}
        onPrimaryAction={onOpenTicket}
        variant="critical"
      />
    );
  }

  return (
    <AlertV2
      title={content.alerts.teamInterventionResolved.title}
      description={content.alerts.teamInterventionResolved.description}
      primaryButtonLabel={content.alerts.teamInterventionResolved.buttonLabel}
      onPrimaryAction={onOpenTicket}
      variant="success"
    />
  );
};

const Banners = ({ children }: { children?: React.ReactNode }) => <>{children}</>;

const Main = ({ children, testId }: { children?: React.ReactNode; testId?: string }) => (
  <Styles.Container data-testid={testId}>
    <Styles.Adapter>
      <Styles.TitleAndColumnsContainer>
        {children}
      </Styles.TitleAndColumnsContainer>
    </Styles.Adapter>
  </Styles.Container>
);

const Alerts = ({ children }: { children?: React.ReactNode }) => <>{children}</>;

const Summary = ({
  content,
  loading = false,
  testId = 'summary',
  children,
}: SummaryProps) => (
  <SummaryContext.Provider value={{ content, loading }}>
    {children({
      Banners,
      Main: function MainWithTestId(props: { children?: React.ReactNode }) {
        return <Main testId={testId} {...props} />;
      },
      Alerts,
    })}
  </SummaryContext.Provider>
);

type SummaryWithStaticProps = React.FC<SummaryProps> & {
  Banner: typeof Banner;
  Title: typeof Title;
  Metrics: typeof Metrics;
  TeamAlert: typeof TeamAlert;
  ToggleRow: typeof ToggleRow;
};

const SummaryWithErrorBoundary = withErrorBoundary(Summary, 'Summary') as unknown as SummaryWithStaticProps;

SummaryWithErrorBoundary.Banner = Banner;
SummaryWithErrorBoundary.Title = Title;
SummaryWithErrorBoundary.Metrics = Metrics;
SummaryWithErrorBoundary.TeamAlert = TeamAlert;
SummaryWithErrorBoundary.ToggleRow = ToggleRow;

export default SummaryWithErrorBoundary;
